import { useEffect, useMemo, useState } from "react";
import { ProductFruits } from "react-product-fruits";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { dropdownIcon } from "../../../../../../utility-functions/assetLoader";

const OnboardingChecklist = () => {
  const { canMakeCalls, canCreateEditUsers, canCreateEditContactsAndLists } =
    useSelector((state) => state?.auth?.permissions);
  const accountDetails = useSelector((state) => state?.account?.value);
  const userRoles = useSelector((state) => state?.auth?.userRoles);
  const virtualNumbers = useSelector((state) => state?.virtualNumber?.value);

  const navigate = useNavigate();

  const [isChecklistVisible, setIsChecklistVisible] = useState(false);
  const [isEligibleForChecklist, setIsEligibleForChecklist] = useState(false);
  const [checklistId, setChecklistId] = useState(null);

  const pfUserInfo = useMemo(() => {
    let role = "";
    if (canMakeCalls && accountDetails?.data?.license === "Plan") role += "12";
    if (canCreateEditContactsAndLists) role += "3";
    if (canCreateEditUsers) role += "4";
    role = userRoles?.reduce((acc, cur) => acc + ", " + cur.title, role)

    let providers = new Set();
    virtualNumbers?.outbound_virtual_numbers?.forEach(vn => providers.add(vn.provider))
    virtualNumbers?.inbound_virtual_numbers?.forEach(vn => providers.add(vn.provider))
    virtualNumbers?.team_virtual_numbers?.forEach(vn => providers.add(vn.provider))

    return {
      username: accountDetails?.data?.email,
      email: accountDetails?.data?.email,
      firstname: accountDetails?.data?.first_name,
      lastname: accountDetails?.data?.last_name,
      role,
      signUpAt: accountDetails?.data?.number?.created_at,
      props: {
        providers: Array.from(providers)
      }
    };
  }, [
    accountDetails,
    userRoles,
    virtualNumbers,
    canMakeCalls,
    canCreateEditContactsAndLists,
    canCreateEditUsers,
  ]);

  useEffect(() => {
    const checklistElem = document.querySelector(
      "#pf-quick-start-menu [data-main-styles=true]"
    );
    if (checklistElem) {
      checklistElem.style.display = isChecklistVisible ? "flex" : "none";
    }
  }, [isChecklistVisible]);

  useEffect(() => {
    window.addEventListener(
      "productfruits_ready",
      handleSetChecklistEligibility
    );

    return () =>
      window.removeEventListener(
        "productfruits_ready",
        handleSetChecklistEligibility
      );
  }, [pfUserInfo]);

  useEffect(() => {
    if (isChecklistVisible) {
      window.addEventListener("click", handleCloseChecklistOnClickAway);
    }

    return () => {
      window.removeEventListener("click", handleCloseChecklistOnClickAway);
    };
  }, [isChecklistVisible]);

  useEffect(() => {
    const setExtra = (px) => {
      document.documentElement.style.setProperty("--pf-banner-height", `${px}px`);
    };


    const wire = () => {
      window.productFruits?.api?.announcementsV2?.listen?.("banner-spacing", (info) => {
        const place = info?.place ?? "top";
        if (place !== "top") return;
        const hasBanner = (info?.desiredMargin ?? 0) > 0;
        setExtra(hasBanner ? 55 : 0);
      });
    };

    window.addEventListener("productfruits_ready", wire);
    if (window.productFruits?.api) wire();

    return () => {
      window.removeEventListener("productfruits_ready", wire);
    };
  }, []);

  const handleCloseChecklistOnClickAway = (e) => {
    const path = e.composedPath()?.map((pathItem) => {
      let newPathItem = null;
      newPathItem =
        pathItem?.tagName?.toLowerCase() +
        (pathItem?.className ? "." + pathItem?.className : "");
      return newPathItem;
    });
    if (
      !path?.length ||
      (!path?.includes("div.pf-quick-start-menu") &&
        !path.includes("div.pf-quick-start"))
    ) {
      setIsChecklistVisible(false);
      window.removeEventListener("click", handleCloseChecklistOnClickAway);
    }
  };

  const handleSetChecklistEligibility = async () => {
    console.log("Product Fruits is ready!");
    let isEligible = "not_eligible";
    const checklists =
      window.productFruits?.checklistsFactory?.config?.checklists;
    if (Array.isArray(checklists)) {
      const checklistId = checklists.find(
        (elem) => elem.name?.split(" - ")?.[1] === pfUserInfo?.role
      )?.id;
      setChecklistId(checklistId);
      if (checklistId) {
        isEligible =
          await window.productFruits?.api?.checklists?.isChecklistEligible(
            checklistId
          );
      }
    }
    console.log("isEligible", isEligible);
    setIsEligibleForChecklist(isEligible === "eligible");
  };

  const handleToggleChecklistDisplay = () => {
    if (!isChecklistVisible) {
      const checklistElem = document.querySelector(
        "#pf-quick-start-menu [data-main-styles=true]"
      );
      if (checklistElem) {
        setIsChecklistVisible(true);
      } else {
        const element = document.getElementById("pf-quick-start-menu");
        window.productFruits?.api?.checklists?.injectToElement(
          checklistId,
          element,
          { checkEligibility: true }
        );
        setIsChecklistVisible(true);

        window.productFruits?.api?.checklists?.listen(
          "item-launched",
          (id, internalId) => {
            setIsChecklistVisible(false);
            console.log("item launched, internalId", internalId);
            if (internalId === "install-chrome-extension-pf") {
              window.open(
                "https://chrome.google.com/webstore/detail/frejun-chrome-dialler-plu/eninbjdhgaccikhclpomppfcclammnoc",
                "_blank"
              );
              window.productFruits?.api?.checklists?.markItemAsDone(
                "install-chrome-extension-pf",
                false
              );
            }
            else if (internalId === "make-call-pf") {
              const dialerElem = document.getElementById("top-nav-dialer");
              dialerElem?.click();
            } else if (internalId === "create-contact-list-pf") {
              navigate("/contact-lists");
            } else if (internalId === "invite-team-members-pf") {
              navigate("/users-and-teams");
            }
          }
        );

        window.productFruits?.api?.checklists?.listen(
          "dismissed",
          (checklistId) => {
            console.log(
              "The checklist was dismissed by the user, id:",
              checklistId
            );
            setIsChecklistVisible(false);
            setIsEligibleForChecklist(false);
          }
        );
      }
    } else {
      setIsChecklistVisible(false);
    }
  };

  const getChecklistItemsCount = () => {
    const checklists = window.productFruits?.checklistsFactory?.config?.checklists;
    const result = {
      total: 0,
      completed: 0,
    };
    if (Array.isArray(checklists)) {
      const checklist = checklists.find((elem) => elem.id === checklistId);
      const completedItems =
        checklist?.items?.filter(
          (item) => "state" in item && item["state"] === "done"
        ) ?? [];
      if (checklist) {
        result.total = checklist.items?.length;
        result.completed = completedItems.length;
      }
    }
    return result;
  };

  return (
    <>
      <ProductFruits
        workspaceCode={process.env.REACT_APP_PF_WORKSPACE_CODE}
        language="en"
        user={pfUserInfo}
        debug={true}
      />
      {isEligibleForChecklist && (
        <>
          <div
            role="button"
            className="pf-quick-start"
            id="pf-quick-start"
            onClick={handleToggleChecklistDisplay}
          >
            <div className="t7 medium-font">
              <div
                className="circle-container"
                style={{
                  background: `conic-gradient(
                  #34CB65 0% ${(getChecklistItemsCount().completed * 100) / getChecklistItemsCount().total}%,       
                  #E4E4E7 ${(getChecklistItemsCount().completed * 100) / getChecklistItemsCount().total}% 100%
                )`,
                }}
              >
                <div className="circle-inner">
                  {getChecklistItemsCount().completed}/
                  {getChecklistItemsCount().total}
                </div>
              </div>
              Quick start
              <span
                style={{
                  transform: isChecklistVisible
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
              >
                {dropdownIcon()}
              </span>
            </div>
          </div>
          <div
            id="pf-quick-start-menu"
            className="pf-quick-start-menu"
            style={{ position: "relative" }}
          ></div>
        </>
      )}
    </>
  );
};

export default OnboardingChecklist;
